import React, { Fragment, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import Typography from '@mui/material/Typography';
import CustomDatePicker from 'app/components/elements/InputDate';
import dayjs from 'dayjs';
import { TDateCommunication, fileResCommunication, SeizureReports } from '../type';
import { CellDataType, PrimaryTableHeader } from 'app/components/elements/TableInfiniteScroll';
import { compact } from 'lodash';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Lead, TNote } from 'types/user';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ROLE } from 'const/common';
import { renderTitleLead } from 'utils/renderTitleLead';
import DeleteIcon from '@mui/icons-material/Delete';
import { DialogConfirm } from '../../../../components/elements/DialogConfirm';
import { useDimension } from 'hooks/useDimension';
import { useNavigate } from 'react-router-dom';

const renderUserName = (user?: {
  firstName?: string | null;
  lastName?: string | null;
  role?: string;
}) => {
  if (!user) return '';
  if (user.role === ROLE.ROOT_ADMIN) return 'Master Admin';
  if (user.firstName || user.lastName) {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
  return 'Admin';
};
const renderContentTableUpdates = (
  rows: (TNote & { lead: Partial<Lead> })[],
  dataClientSelected: fileResCommunication | undefined,
  handleDeleteNote: (value: { idNote: number; lead?: Partial<Lead> }) => void
) => {
  const handleButtonClickCopy = async (id: number) => {
    const itemNote = rows.find((item) => Number(item.id) === id);
    if (!itemNote) {
      alert('Cannot copy note');
      return;
    }
    const titleLead = renderTitleLead(itemNote.lead);
    const noteText = `${dayjs(itemNote.updatedAt).format('M/D/YYYY, h:mm A')} - ${itemNote.content}`;
    const textCopyArray = `${titleLead}\n${noteText}`;
    try {
      await navigator.clipboard.writeText(textCopyArray);
    } catch (error) {
      console.error('Failed to copy text to clipboard: ', error);
    }
  };
  return rows.map((item: TNote) => {
    if (item.deletedAt || !item?.content) return;
    const lead = dataClientSelected?.leads.find(
      (itemLead) => Number(itemLead.id) === Number(item.leadId)
    );
    return {
      updatedAt: {
        value: <Typography fontSize={'13px'}>{renderUserName(item.user)}</Typography>
      },
      date: {
        value: (
          <Typography fontSize={'13px'}>
            {dayjs(item?.updatedAt).format('h:mma | YYYY/MM/DD')}
          </Typography>
        )
      },
      lead: {
        value: <Typography fontSize={'13px'}>{lead ? renderTitleLead(lead) : ''}</Typography>
      },
      details: {
        value: (
          <Typography fontSize={'13px'} color={item.deletedAt || !item?.content ? 'red' : 'unset'}>
            {item.deletedAt || !item?.content ? `Remove note` : item?.content}
          </Typography>
        )
      },
      action: {
        value: (
          <Stack flexDirection={'row'} gap={2}>
            <IconButton onClick={() => handleButtonClickCopy(Number(item.id))}>
              <ContentCopyOutlinedIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                handleDeleteNote({ idNote: Number(item.id), lead: lead });
              }}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        )
      }
    };
  });
};

const renderContentTableReport = ({
  dataClientSelected,
  handleDelete
}: {
  dataClientSelected: fileResCommunication | undefined;
  handleDelete?: (idReport: number, fileId: number) => void;
}) => {
  const handleButtonClickCopy = async (id: number) => {
    const item = dataClientSelected?.seizureReports?.find((item) => Number(item.id) === id);
    if (!item) {
      alert('Cannot copy seizure report');
      return;
    }
    const textCopyArray = `
    ******** SEIZURE REPORT ********
    
    File Number: ${dataClientSelected?.fileNumber}
    Debtor Name: ${dataClientSelected?.firstName} ${dataClientSelected?.lastName}
    Seize Date: ${dayjs(item?.createdAt).format('YYYY/MM/DD')}
    Seized Address: ${item?.postalCode || ''} ${item?.addressLead?.address || item?.otherAddress}
    Customers POR: ${item?.customersLead?.address || item?.otherCustomerPOR}
    Removed to: ${item?.removedToLocation?.value || item?.removedToOther}
    KM: ${item?.kilometers}
    Keys: ${item?.hasKey ? 'Yes' : 'No'}
    Runs: ${item?.run ? 'Yes' : 'No'}
    Signed VSA: ${item.signedVsa ? 'Yes' : 'No'}
    Possible Redemption: ${item?.possibleRedemption ? 'Yes' : 'No'}
    Condition Report: ${item?.conditionReport}
    Towing company used: ${item?.towingCompany}
    Transport required: ${item?.transportRequired ? 'Yes' : 'No'}
    Other bills incurred:  ${item?.billsIncurred ? 'Yes' : 'No'}
    `;
    try {
      await navigator.clipboard.writeText(textCopyArray);
    } catch (error) {
      console.error('Failed to copy text to clipboard: ', error);
    }
  };
  return dataClientSelected?.seizureReports?.map((item: SeizureReports) => {
    return {
      name: {
        value: (
          <Typography fontSize={'13px'}>
            {dataClientSelected?.firstName} {dataClientSelected?.lastName}
          </Typography>
        )
      },
      date: {
        value: (
          <Typography fontSize={'13px'}>{dayjs(item?.createdAt).format('YYYY/MM/DD')}</Typography>
        )
      },
      address: {
        value: (
          <Typography fontSize={'13px'}>
            {item?.postalCode} {item?.addressLead?.address || item?.otherAddress}
          </Typography>
        )
      },
      customersPor: {
        value: (
          <Typography fontSize={'13px'}>
            {item?.customersLead?.address || item?.otherCustomerPOR}
          </Typography>
        )
      },
      removeTo: {
        value: (
          <Typography fontSize={'13px'}>
            {item?.removedToLocation?.value || item?.removedToOther}
          </Typography>
        )
      },
      km: {
        value: <Typography fontSize={'13px'}>{item?.kilometers}</Typography>
      },
      keys: {
        value: <Typography fontSize={'13px'}>{item?.hasKey ? 'Yes' : 'No'}</Typography>
      },
      run: {
        value: <Typography fontSize={'13px'}>{item?.run ? 'Yes' : 'No'}</Typography>
      },
      possibleRedemption: {
        value: <Typography fontSize={'13px'}>{item?.possibleRedemption ? 'Yes' : 'No'}</Typography>
      },
      conditionReport: {
        value: <Typography fontSize={'13px'}>{item?.conditionReport}</Typography>
      },
      towingCompany: {
        value: <Typography fontSize={'13px'}>{item?.towingCompany}</Typography>
      },
      transportRequired: {
        value: (
          <Typography fontSize={'13px'}>
            {item?.transportRequired ? item?.otherTransport : 'No'}
          </Typography>
        )
      },
      billsIncurred: {
        value: (
          <Typography fontSize={'13px'}>{item?.billsIncurred ? item?.otherBills : 'No'}</Typography>
        )
      },
      action: {
        value: (
          <Stack flexDirection={'row'} gap={2}>
            <IconButton
              sx={{ padding: '0 8px 0 0' }}
              onClick={() => handleButtonClickCopy(Number(item.id))}>
              <ContentCopyOutlinedIcon />
            </IconButton>
            {handleDelete && (
              <IconButton
                sx={{ padding: '0 8px 0 0' }}
                onClick={() => handleDelete(Number(item.id), Number(dataClientSelected?.id))}>
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
        )
      }
    };
  });
};

type ActiveTabProps = {
  handleChangeDate: (startDate?: string | null, endDate?: string | null) => void;
  headers: PrimaryTableHeader[];
  rows: fileResCommunication[];
  label: string;
  date: TDateCommunication | null;
  hasMore: boolean;
  fetchMoreData: () => void;
  dataClientSelected: fileResCommunication | undefined;
  setDataClientSelected: (value: fileResCommunication | undefined) => void;
  handleDeleteNote: (value: { idNote: number; lead?: Partial<Lead> }) => void;
  handleDeleteReport?: (id: number, fileId: number) => void;
  isReport?: boolean;
  isUpdate?: boolean;
};

export const TableCommunication = ({
  isReport,
  isUpdate,
  rows,
  label,
  headers,
  handleChangeDate,
  date,
  hasMore,
  fetchMoreData,
  dataClientSelected,
  setDataClientSelected,
  handleDeleteNote,
  handleDeleteReport: handleDelete
}: ActiveTabProps) => {
  const navigate = useNavigate();
  const { height } = useDimension();

  const [filePhotoView, settFilePhotoView] = useState<fileResCommunication | null>(null);
  const handleChooseClient = useCallback(
    (id: number) => {
      const dataSelected = rows.find((item) => Number(item.id) === Number(id));
      setDataClientSelected(dataSelected);
    },
    [rows, setDataClientSelected]
  );

  const updatedNotes = useMemo(() => {
    const notes =
      dataClientSelected?.leads?.flatMap(
        (item) => item.notes?.map((note) => ({ ...note, lead: item })) || []
      ) || [];

    return notes.sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
  }, [dataClientSelected]);
  return (
    <WrapperBox>
      <Stack p={2} flexDirection={'row'} justifyContent={'space-between'}>
        <Typography fontSize={18} fontWeight={700} marginBottom={2.5}>
          {label}
        </Typography>
        <Stack direction="row" alignItems={'center'} gap={1.5}>
          <TextDateStyled>From</TextDateStyled>
          <CustomDatePicker
            title={''}
            onChange={(value) => {
              const dateStartConvert = value ? String(value) : '';
              handleChangeDate(dateStartConvert, date?.endDate);
            }}
            value={date?.startDate ? dayjs(date?.startDate) : null}
            maxDate={date?.endDate ? dayjs(date?.endDate) : undefined}
          />
          <TextDateStyled>To</TextDateStyled>
          <CustomDatePicker
            title={''}
            onChange={(value) => {
              const dateStartConvert = value ? String(value) : '';
              handleChangeDate(date?.startDate, dateStartConvert);
            }}
            value={date?.endDate ? dayjs(date?.endDate) : null}
            minDate={date?.startDate ? dayjs(date?.startDate) : undefined}
          />
        </Stack>
      </Stack>

      <WrapperContainer>
        <ListClientWrapper width={'25%'} height={height - 300} overflow={'scroll'} id={'divScroll'}>
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchMoreData}
            hasMore={hasMore}
            scrollableTarget={'divScroll'}
            loader={
              <Box p={1}>
                <CircularProgress />
              </Box>
            }>
            {rows.map((item, index) => {
              const userAssign = compact(
                item.dispatchFiles?.map((item) => renderUserName(item.user))
              );
              return (
                <WrapperItem
                  style={{ background: item.id === dataClientSelected?.id ? '#F1F1E8' : '#FFFFFF' }}
                  key={index}
                  onClick={() => handleChooseClient && handleChooseClient(item.id)}>
                  <Stack gap={0.8}>
                    <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                      <Stack
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '4px',
                          background: '#22374A'
                        }}
                      />
                      <Typography fontSize={'16px'} fontWeight={500}>
                        {item?.clientOption?.value}
                      </Typography>
                    </Stack>
                    <Typography fontSize={'13px'} fontWeight={500}>
                      {`File Number: ${item.fileNumber}`}
                    </Typography>
                  </Stack>
                  <Stack style={{ alignItems: 'flex-start', width: '40%' }}>
                    <Typography fontSize={'12px'} color={'#7F7F7F'} fontWeight={400}>
                      {dayjs(
                        isReport && item?.seizureReports && item?.seizureReports?.length > 0
                          ? item.seizureReports[0].createdAt
                          : item.lastNoteAt || item.lastCommunicationAt || item.updatedAt
                      ).format('YYYY-MM-DD hh:mm A')}
                    </Typography>
                    {isReport ? (
                      <Typography fontSize={'12px'} color={'#7F7F7F'} fontWeight={400}>
                        Seizure Report by{' '}
                        {item?.seizureReports &&
                        item?.seizureReports?.length > 0 &&
                        item.seizureReports[0].createdBy?.id
                          ? renderUserName(item.seizureReports[0]?.createdBy)
                          : userAssign.join(', ')}
                      </Typography>
                    ) : isUpdate ? (
                      <Typography fontSize={'12px'} color={'#7F7F7F'} fontWeight={400}>
                        Update added by {userAssign.join(', ')}
                      </Typography>
                    ) : (
                      <Typography fontSize={'12px'} color={'#7F7F7F'} fontWeight={400}>
                        Out of leads report submitted by {userAssign.join(', ')}
                      </Typography>
                    )}
                  </Stack>
                </WrapperItem>
              );
            })}
          </InfiniteScroll>
        </ListClientWrapper>
        <ListClientWrapper
          width={'75%'}
          sx={{ borderRightWidth: 0 }}
          p={2}
          height={height - 300}
          overflow={'scroll'}
          id={'divScroll'}>
          <Stack gap={1.25}>
            <Stack direction="row" justifyContent={'space-between'}>
              <Stack>
                <Typography fontSize={16} fontWeight={600}>
                  {dataClientSelected?.clientOption?.value}
                </Typography>
                <Typography
                  onClick={() => {
                    navigate(`/admin/files/${dataClientSelected?.id}`);
                  }}
                  style={{ textDecorationLine: 'underline', cursor: 'pointer' }}
                  fontSize={16}
                  fontWeight={600}>
                  {dataClientSelected?.fileNumber}
                </Typography>
              </Stack>
              {isReport && (
                <Stack flexDirection={'row'} gap={2}>
                  <Button
                    onClick={() => {
                      if (
                        dataClientSelected?.seizureReports?.length &&
                        dataClientSelected?.seizureReports[0]?.vsaDocuments[0]?.url
                      ) {
                        window.open(
                          dataClientSelected?.seizureReports[0]?.vsaDocuments[0]?.url,
                          'blank'
                        );
                      }
                    }}
                    disabled={
                      !(
                        dataClientSelected?.seizureReports?.length &&
                        dataClientSelected?.seizureReports[0]?.vsaDocuments[0]?.url
                      )
                    }
                    style={{
                      padding: '8px 20px',
                      borderRadius: '10px',
                      border: '1px solid #22374A'
                    }}>
                    View VSA
                  </Button>
                  <Button
                    onClick={() => dataClientSelected && settFilePhotoView(dataClientSelected)}
                    disabled={
                      !(
                        dataClientSelected?.seizureReports?.length &&
                        dataClientSelected?.seizureReports[0]?.seizureReportPhotos.length
                      )
                    }
                    style={{
                      padding: '8px 20px',
                      borderRadius: '10px',
                      border: '1px solid #22374A'
                    }}>
                    View Photo
                  </Button>
                </Stack>
              )}
            </Stack>
            <TableContainer>
              <Table style={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    {headers.map((header: PrimaryTableHeader, index: number) => {
                      return (
                        <Fragment key={index}>
                          <TableCellStyled
                            onClick={() => {
                              header.onClick &&
                                dataClientSelected?.id &&
                                header.onClick(dataClientSelected.id);
                            }}
                            width={header.width}>
                            {header.label}
                          </TableCellStyled>
                        </Fragment>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isReport
                    ? renderContentTableReport({ dataClientSelected, handleDelete })?.map(
                        (itemNote, index) => {
                          if (!itemNote) return;
                          return (
                            <TableRowStyled
                              sx={{
                                backgroundColor: '#fff'
                              }}
                              key={index}
                              onClick={() => {}}>
                              {Object.values(itemNote)?.map(
                                (record: CellDataType, colIndex: number) => {
                                  return (
                                    <Fragment key={colIndex}>
                                      <TableCellStyled>{record.value}</TableCellStyled>
                                    </Fragment>
                                  );
                                }
                              )}
                            </TableRowStyled>
                          );
                        }
                      )
                    : renderContentTableUpdates(
                        updatedNotes,
                        dataClientSelected,
                        handleDeleteNote
                      ).map((itemNote, index) => {
                        if (!itemNote) return;
                        return (
                          <TableRowStyled
                            sx={{
                              backgroundColor: '#fff'
                            }}
                            key={index}
                            onClick={() => {}}>
                            {Object.values(itemNote)?.map(
                              (record: CellDataType, colIndex: number) => {
                                return (
                                  <Fragment key={colIndex}>
                                    <TableCellStyled>{record.value}</TableCellStyled>
                                  </Fragment>
                                );
                              }
                            )}
                          </TableRowStyled>
                        );
                      })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </ListClientWrapper>
      </WrapperContainer>
      <DialogConfirm
        maxWidthDialog={'lg'}
        contentStyle={{ maxWidth: 1110 }}
        isOpen={!!filePhotoView}
        width={'900px'}
        onClose={() => settFilePhotoView(null)}>
        <Stack
          width={'100%'}
          height={'100%'}
          alignItems={'flex-start'}
          justifyContent={'flex-start'}>
          <Stack
            flexDirection={'row'}
            gap={3.5}
            alignItems={'flex-start'}
            justifyContent={'flex-start'}>
            <Stack alignItems={'flex-start'} justifyContent={'flex-start'}>
              <Typography fontSize={'20px'} fontWeight={500} color={'#0E0E0E'}>
                {dataClientSelected?.clientOption?.value}
              </Typography>
              <Typography fontSize={'16px'} fontWeight={500} color={'#0E0E0E'}>
                {`File Number: ${dataClientSelected?.fileNumber}`}
              </Typography>
            </Stack>
            {/*<Button*/}
            {/*  onClick={async () => {*/}
            {/*    const listFileDownLoad = compact(*/}
            {/*      dataClientSelected?.seizureReports?.flatMap((item) =>*/}
            {/*        item.seizureReportPhotos.map((itemPhoto) => itemPhoto.url)*/}
            {/*      )*/}
            {/*    );*/}
            {/*    if (!listFileDownLoad.length) return;*/}
            {/*    await handleDownload(listFileDownLoad);*/}
            {/*  }}*/}
            {/*  style={{*/}
            {/*    padding: '10px 22px',*/}
            {/*    borderRadius: '10px',*/}
            {/*    alignItems: 'center',*/}
            {/*    justifyContent: 'center',*/}
            {/*    border: '1px solid #22374A',*/}
            {/*    cursor: 'pointer'*/}
            {/*  }}>*/}
            {/*  Download All*/}
            {/*</Button>*/}
          </Stack>
          <Stack>
            {dataClientSelected?.seizureReports?.flatMap((report) =>
              report.seizureReportPhotos.map((itemPhoto) => {
                console.log(itemPhoto);
                const url = itemPhoto.url || '';
                const isImage =
                  url.toLowerCase().endsWith('.jpg') ||
                  url.toLowerCase().endsWith('.jpeg') ||
                  url.toLowerCase().endsWith('.png') ||
                  url.toLowerCase().endsWith('.gif') ||
                  url.toLowerCase().endsWith('.webp');

                const isPDF = url.toLowerCase().endsWith('.pdf');

                return (
                  <Stack
                    key={url}
                    style={{
                      padding: '20px 0',
                      cursor: 'pointer'
                    }}
                    onClick={() => window.open(url, '_blank')}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      {isImage ? (
                        <img
                          src={url}
                          alt="Preview"
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }}
                        />
                      ) : (
                        <PictureAsPdfIcon sx={{ width: 60, height: 60, color: '#d32f2f' }} />
                      )}
                      <Typography fontSize="14px" fontWeight={500} color="#0E0E0E">
                        {isImage ? 'View Image' : isPDF ? 'View PDF' : 'View File'}
                      </Typography>
                    </Stack>
                  </Stack>
                );
              })
            )}
          </Stack>
        </Stack>
      </DialogConfirm>
    </WrapperBox>
  );
};

const ListClientWrapper = styled(Stack)(() => ({
  border: '1px solid #686E7D',
  borderLeftWidth: 0
}));
const WrapperContainer = styled(Stack)(() => ({
  width: '100%',
  flexDirection: 'row'
}));
const WrapperItem = styled(Stack)(() => ({
  borderBottom: '1px solid #686E7D',
  width: '100%',
  padding: '20px',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  cursor: 'pointer',
  ' &:hover': {
    opacity: 0.8,
    background: '#F1F1E8'
  }
}));

const WrapperBox = styled(Stack)(() => ({
  border: '1px solid #686E7D',
  padding: '14px 0 10px 0',
  borderRadius: '16px',
  flexDirection: 'column',
  marginTop: '20px'
}));

const TextDateStyled = styled(Typography)(() => ({
  fontSize: '13px',
  fontWeight: 400,
  color: '#0A0A0A'
}));
const TableRowStyled = styled(TableRow)(() => ({
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8
  }
}));
const TableCellStyled = styled(TableCell)(() => ({
  padding: '14px 8px'
}));
